// ============================================
// PA Documents & Compliance Page
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import socketService from '../../services/socket';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { paMenuItems } from './paNav';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import NotificationBell from '../../components/common/NotificationBell';

const PADocuments = () => {
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const { success, error: showError } = useToast();
    const [docs, setDocs] = useState({});
    const [hasDBS, setHasDBS] = useState(true);
    const [uploading, setUploading] = useState(null);
    const [expiries, setExpiries] = useState({});

    const primaryDocs = [
        {
            key: 'identityDocument',
            label: 'Identity Document',
            desc: 'Passport, National ID or Driving Licence',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"></rect><circle cx="9" cy="10" r="2"></circle><line x1="15" y1="8" x2="17" y2="8"></line><line x1="15" y1="12" x2="17" y2="12"></line><line x1="7" y1="16" x2="17" y2="16"></line></svg>`,
            color: 'from-blue-500 to-blue-600',
            required: true,
        },
        {
            key: 'dbsCertificate',
            label: 'DBS Certificate',
            desc: 'Enhanced DBS check certificate',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`,
            color: 'from-violet-500 to-purple-600',
            required: true,
            onlyWithDBS: true,
        },
        {
            key: 'safeguardingCourse',
            label: 'Safeguarding Course',
            desc: 'Online safeguarding certificate (annual renewal)',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>`,
            color: 'from-emerald-500 to-teal-600',
            required: false,
        },
    ];

    const fallbackDocs = [
        {
            key: 'drivingLicense',
            label: 'Provisional/Full Driving Licence',
            desc: 'Alternative to DBS',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"></rect><circle cx="9" cy="10" r="2"></circle><line x1="15" y1="8" x2="17" y2="8"></line><line x1="15" y1="12" x2="17" y2="12"></line><line x1="7" y1="16" x2="17" y2="16"></line></svg>`,
            color: 'from-sky-500 to-cyan-600',
        },
        {
            key: 'arc',
            label: 'ARC (if applicable)',
            desc: 'Application Registration Card',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>`,
            color: 'from-amber-500 to-orange-600',
        },
        {
            key: 'passportFront',
            label: 'Passport Front Page',
            desc: 'Photo page of passport (if applicable)',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>`,
            color: 'from-rose-500 to-pink-600',
        },
        {
            key: 'bankStatement',
            label: 'Last 3 Months Bank Statement',
            desc: 'Recent bank statements for verification',
            icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>`,
            color: 'from-indigo-500 to-indigo-600',
        },
    ];

    const fetchDocs = useCallback(async () => {
        try {
            const res = await api.get('/pa/documents');
            setDocs(res.data.data || {});
            setHasDBS(res.data.hasDBS !== false);
        } catch (err) { console.error('Fetch PA docs error:', err); }
    }, []);

    // Stable refs for toast functions to prevent effect re-registration
    const successRef = React.useRef(success);
    const showErrorRef = React.useRef(showError);
    React.useEffect(() => { successRef.current = success; }, [success]);
    React.useEffect(() => { showErrorRef.current = showError; }, [showError]);

    useEffect(() => {
        fetchDocs();

        // pa:doc:updated → refresh docs + show toast based on status
        const handleDocUpdate = (d) => {
            fetchDocs();
            if (d?.status === 'approved') {
                successRef.current('Your document has been approved!');
            } else if (d?.status === 'rejected') {
                showErrorRef.current('A document was rejected. Please re-upload.');
            }
        };

        // notification:new → catch rejection/approval alerts for immediate toast
        const handleNotification = (n) => {
            if (n?.type === 'document_update') {
                if (n.data?.status === 'approved') {
                    successRef.current(n.title || 'Document approved!');
                } else if (n.data?.status === 'rejected') {
                    showErrorRef.current(n.message || 'A document was rejected. Please re-upload.');
                    fetchDocs(); // Also refresh to show rejection reason
                }
            }
        };

        socketService.on('pa:doc:updated', handleDocUpdate);
        socketService.on('notification:new', handleNotification);

        return () => {
            socketService.off('pa:doc:updated', handleDocUpdate);
            socketService.off('notification:new', handleNotification);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchDocs]); // fetchDocs is stable via useCallback

    const handleToggleDBS = async () => {
        const newValue = !hasDBS;
        setHasDBS(newValue);
        try {
            await api.patch('/pa/profile', { hasDBS: newValue });
        } catch (err) { console.error('Toggle DBS error:', err); }
    };

    const handleUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const expiryDate = expiries[type];
        const requiresExpiry = true;
        if (requiresExpiry && !expiryDate) {
            showError(`Please select expiry date first`);
            return;
        }

        setUploading(type);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        if (expiryDate) formData.append('expiryDate', expiryDate);

        try {
            await api.post('/pa/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
            success('Document uploaded successfully!');
            fetchDocs();
        } catch (err) {
            showError(err.response?.data?.message || 'Upload failed');
        } finally { setUploading(null); }
    };

    const getDocUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        if (url.startsWith('/uploads/')) return '/api' + url;
        return url;
    };

    const getStatusStyles = (doc) => {
        if (!doc || !doc.url) return 'bg-slate-50 dark:bg-slate-700/30 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-white/10 ring-slate-500/10';
        if (doc.expiryDate && new Date(doc.expiryDate) < new Date()) return 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20 ring-red-500/20';
        switch (doc.status) {
            case 'approved': return 'bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-500/20 ring-teal-500/20';
            case 'pending': return 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20 ring-amber-500/20';
            case 'rejected': return 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20 ring-red-500/20';
            default: return 'bg-slate-50 dark:bg-slate-700/30 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-white/10 ring-slate-500/10';
        }
    };

    const getDocLabel = (doc) => {
        if (!doc || !doc.url) return 'NOT FILED';
        if (doc.expiryDate && new Date(doc.expiryDate) < new Date()) return 'EXPIRED';
        if (doc.status === 'pending') return 'IN REVIEW';
        if (doc.status === 'approved') return 'APPROVED';
        if (doc.status === 'rejected') return 'REJECTED';
        return 'NOT FILED';
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const isExpiringSoon = (dateStr) => {
        if (!dateStr) return false;
        const expiry = new Date(dateStr);
        const now = new Date();
        const daysLeft = (expiry - now) / (1000 * 60 * 60 * 24);
        return daysLeft <= 30 && daysLeft >= 0;
    };

    const isExpired = (dateStr) => {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    };

    const renderDocCard = (doc) => (
        <div key={doc.key} className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl p-6 sm:p-8 rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 flex flex-col sm:flex-row items-center justify-between group hover:shadow-2xl hover:shadow-violet-500/10 dark:hover:shadow-violet-500/5 transition-all duration-500 overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto text-center sm:text-left">
                <div className={`w-16 h-16 rounded-[24px] bg-gradient-to-br ${doc.color} flex items-center justify-center text-white p-4 shadow-lg shadow-black/10 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <span className="w-full h-full block" dangerouslySetInnerHTML={{ __html: doc.icon }} />
                </div>
                <div>
                    <h4 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight mb-1">{doc.label}</h4>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tracking-wider mb-2">{doc.desc}</p>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className={`inline-flex items-center px-4 py-1 rounded-full text-[10px] font-black uppercase border ring-4 ring-offset-0 transition-colors ${getStatusStyles(docs[doc.key])}`}>
                            {getDocLabel(docs[doc.key])}
                        </div>
                        {docs[doc.key]?.expiryDate && (
                            <>
                                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    Exp: {formatDate(docs[doc.key].expiryDate)}
                                </span>
                                {isExpired(docs[doc.key].expiryDate) && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest bg-rose-500/10 text-rose-600 border border-rose-500/30 shadow-[0_0_15px_rgba(244,63,94,0.3)] animate-pulse"><svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> EXPIRED</span>
                                )}
                                {isExpiringSoon(docs[doc.key].expiryDate) && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest bg-amber-500/10 text-amber-600 border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.2)] animate-pulse"><svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> EXPIRING SOON</span>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 mt-6 sm:mt-0 w-full sm:w-auto">
                <div className="flex flex-col gap-1 w-full sm:w-32">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Expiry Date</label>
                    <input type="date" value={expiries[doc.key] || ''} onChange={(e) => setExpiries({ ...expiries, [doc.key]: e.target.value })}
                        className="px-3 py-2 rounded-xl border border-violet-200 dark:border-violet-500/20 text-[11px] font-black text-violet-800 dark:text-violet-300 outline-none focus:border-violet-500 dark:focus:border-violet-400 transition-all bg-violet-50/50 dark:bg-violet-500/10 shadow-inner accent-violet-500 [color-scheme:light] dark:[color-scheme:dark]" />
                </div>
                <div className="flex flex-col items-center gap-3 w-full sm:w-auto mt-4 sm:mt-0 pt-4 sm:pt-4">
                    {docs[doc.key]?.status === 'rejected' && docs[doc.key]?.rejectionReason && (
                        <div className="w-full px-4 py-2.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-[10px] font-bold text-red-600 dark:text-red-400 leading-relaxed">
                            <span className="font-black uppercase tracking-widest">Reason:</span> {docs[doc.key].rejectionReason}
                        </div>
                    )}
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        {docs[doc.key]?.url && (
                            <a href={getDocUrl(docs[doc.key].url)} target="_blank" rel="noreferrer"
                                className="p-4 rounded-2xl bg-white/50 dark:bg-slate-800/30 text-slate-400 dark:text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-all border border-white/80 dark:border-white/5 group/view">
                                <svg className="w-5 h-5 group-hover/view:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            </a>
                        )}
                        <label key={`${doc.key}-${docs[doc.key]?.url || 'empty'}-${docs[doc.key]?.status || 'pending'}`} className={`flex-1 sm:flex-none cursor-pointer px-8 py-4 rounded-2xl text-xs font-black transition-all text-center uppercase tracking-widest ${
                            uploading === doc.key 
                                ? 'bg-slate-100 dark:bg-slate-700/30 text-slate-400 dark:text-slate-500'
                                : docs[doc.key]?.status === 'rejected' || (docs[doc.key]?.expiryDate && new Date(docs[doc.key].expiryDate) < new Date())
                                    ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white hover:from-rose-600 hover:to-red-700 shadow-xl shadow-rose-500/20 animate-pulse hover:animate-none'
                                    : docs[doc.key]?.status === 'approved'
                                        ? 'bg-violet-600 dark:bg-violet-500/20 text-white dark:text-violet-300 shadow-xl shadow-violet-500/10 hover:bg-violet-750 dark:hover:bg-violet-500/30 hover:scale-[1.02]'
                                        : docs[doc.key]?.url
                                            ? 'bg-slate-300 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-400 dark:hover:bg-slate-700 shadow-xl shadow-slate-500/5 hover:scale-[1.02]'
                                            : 'bg-slate-900 dark:bg-white/10 text-white hover:bg-violet-600 dark:hover:bg-violet-500/20 shadow-xl shadow-black/10 hover:scale-[1.02]'
                        }`}>
                            {uploading === doc.key 
                                ? 'Uploading...' 
                                : docs[doc.key]?.status === 'rejected'
                                    ? <span className="flex items-center gap-1"><svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Rejected — Re-upload</span>
                                    : (docs[doc.key]?.expiryDate && new Date(docs[doc.key].expiryDate) < new Date())
                                        ? <span className="flex items-center gap-1"><svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Expired — Re-upload</span>
                                        : docs[doc.key]?.status === 'approved'
                                            ? <span className="flex items-center gap-1"><svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> Approved (Update)</span>
                                            : docs[doc.key]?.url 
                                                ? 'Uploaded (Update)' 
                                                : 'Upload'
                            }
                            <input type="file" accept="image/*,application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => handleUpload(e, doc.key)} disabled={uploading === doc.key} />
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );

    const activePrimaryDocs = primaryDocs; // Always show all primary docs

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-50 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative pb-20 sm:pb-0">
            <Sidebar items={paMenuItems} theme="violet" title="IdaCab PA" />
            <div className="flex-1 flex flex-col overflow-hidden">
                <DriverMobileHeader
                    user={user}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    settingsPath="/pa/settings"
                />

                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center justify-between px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-violet-600 to-purple-400 bg-clip-text text-transparent tracking-tighter">Compliance & Documents</h2>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-4xl mx-auto space-y-8">
                        {/* Onboarding Status Hero */}
                        <div className="relative bg-gradient-to-br from-slate-950/90 to-slate-900/90 dark:from-slate-900/80 dark:to-slate-950/80 backdrop-blur-3xl p-6 sm:p-10 rounded-[2.5rem] text-white shadow-2xl shadow-slate-900/40 dark:shadow-black/50 border border-white/5 overflow-hidden group hover:shadow-violet-500/20 transition-all duration-500">
                            <div className="absolute -right-20 -top-20 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl group-hover:bg-violet-500/20 transition-all duration-1000" />
                            <h3 className="text-xl font-black flex items-center gap-3 mb-4 uppercase tracking-tight relative z-10">
                                <span className="w-3 h-3 rounded-full bg-violet-500 animate-pulse shadow-[0_0_15px_rgba(139,92,246,0.5)]" />
                                Onboarding Status
                            </h3>
                            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed font-medium max-w-2xl relative z-10">
                                Upload your mandatory documents below. Admin will review and approve within 24–48 hours.
                            </p>
                        </div>



                        {/* Primary Documents */}
                        <div className="flex items-center gap-3 mt-2">
                            <div className="w-8 h-8 rounded-lg bg-violet-500/10 dark:bg-violet-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                            </div>
                            <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">Required Documents</h3>
                            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                        </div>
                        <div className="grid gap-4 sm:gap-6">
                            {activePrimaryDocs.map(renderDocCard)}
                        </div>

                        {/* Fallback Documents (Alternative to DBS) - Always visible now */}
                            <>
                                <div className="flex items-center gap-3 mt-8">
                                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                    </div>
                                    <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">Alternative Documents (Optional)</h3>
                                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                                </div>
                                <div className="bg-amber-50/50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 rounded-2xl p-4 mb-2">
                                    <p className="text-[11px] font-bold text-amber-700 dark:text-amber-400 leading-relaxed">
                                        These alternative documents are optional. If you don't have a required DBS certificate, you can provide these so we can apply for one on your behalf.
                                    </p>
                                </div>
                                <div className="grid gap-4 sm:gap-6">
                                    {fallbackDocs.map(renderDocCard)}
                                </div>
                            </>
                    </div>
                </main>
            </div>
            <BottomNav items={paMenuItems} />
        </div>
    );
};

export default PADocuments;
